import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiPaperclip, FiSend, FiX } from "react-icons/fi";
import {
  useCreateTicketReply,
  useFetchTicketById,
  useUpdateTicketStatus,
} from "../hooks/useCustomerSupportTicket";
import { useUsersByIds } from "../hooks/useUsers";

const statusLabels = {
  new: "New",
  triaged: "Triaged",
  "in-progress": "In progress",
  waiting: "Waiting",
  resolved: "Resolved",
  closed: "Closed",
};

const statusStyles = {
  new: "bg-slate-100 text-slate-600",
  triaged: "bg-sky-50 text-sky-600",
  "in-progress": "bg-amber-50 text-amber-600",
  waiting: "bg-orange-50 text-orange-600",
  resolved: "bg-emerald-50 text-emerald-600",
  closed: "bg-slate-100 text-slate-500",
};

const categoryStyles = {
  bug: "bg-rose-50 text-rose-500",
  feature: "bg-violet-50 text-violet-500",
  question: "bg-sky-50 text-sky-600",
  incident: "bg-orange-50 text-orange-600",
};

const statusOptions = ["new", "triaged", "in-progress", "waiting", "resolved", "closed"];

const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : "-");

const ATTACHMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.csv,.txt,.gif,.jpg,.jpeg,.png,.webp";

const formatFileSize = (size) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatAuthorName = (author, fallback) => {
  const name = [author?.firstName, author?.lastName].filter(Boolean).join(" ");
  return name || fallback;
};

function AttachmentList({ attachments = [] }) {
  if (!attachments.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {attachments.map((attachment) => (
        <a
          key={attachment.id}
          href={attachment.downloadUrl}
          target="_blank"
          rel="noreferrer"
          download
          className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-50 px-2 py-1 text-[10px] text-cyan-700 hover:underline"
        >
          <FiPaperclip aria-hidden="true" />
          <span className="truncate">{attachment.originalName}</span>
          <span className="text-slate-400">({formatFileSize(attachment.size)})</span>
        </a>
      ))}
    </div>
  );
}

function ActivityMessage({ author, authorType, message, date, attachments }) {
  const isAgent = authorType === "agent";

  return (
    <div className="flex gap-2.5">
      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${isAgent ? "bg-cyan-700" : "bg-sky-500"}`}>
        {isAgent ? "A" : "C"}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[10px]">
          <span className="font-semibold text-slate-700">
            {formatAuthorName(author, isAgent ? "AIZO Support" : "Client")}
          </span>
          <span className="text-slate-400">{formatDate(date)}</span>
        </div>
        <p className="mt-1 whitespace-pre-wrap text-[11px] leading-5 text-slate-600">{message}</p>
        <AttachmentList attachments={attachments} />
      </div>
    </div>
  );
}

function TicketDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const ticketId = Number(id);
  const { data, isLoading, isError } = useFetchTicketById(ticketId);
  const replyMutation = useCreateTicketReply();
  const statusMutation = useUpdateTicketStatus();
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);

  const ticket = data?.ticket || data;
  const replies = ticket?.replies || [];
  const usersById = useUsersByIds([ticket?.userId, ...replies.map((reply) => reply.userId)]);
  const ticketOwner = usersById.get(String(ticket?.userId));
  const canReply = ticket && !["resolved", "closed"].includes(ticket.status);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedMessage = message.trim();

    if ((!trimmedMessage && !attachments.length) || !canReply || replyMutation.isPending) return;

    replyMutation.mutate(
      { id: ticket.id, message: trimmedMessage, attachments },
      { onSuccess: () => { setMessage(""); setAttachments([]); } }
    );
  };

  const handleAttachmentChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setAttachments((current) => {
      const combined = [...current, ...selectedFiles];
      return combined
        .filter((file, index, files) => files.findIndex((candidate) => (
          candidate.name === file.name
          && candidate.size === file.size
          && candidate.lastModified === file.lastModified
        )) === index)
        .slice(0, 5);
    });
    event.target.value = "";
  };

  const removeAttachment = (fileToRemove) => {
    setAttachments((current) => current.filter((file) => file !== fileToRemove));
  };

  const handleStatusChange = (status) => {
    if (status === ticket.status || statusMutation.isPending) return;

    statusMutation.mutate({ id: ticket.id, status });
  };

  if (isLoading) {
    return <p className="text-xs text-slate-400">Loading ticket...</p>;
  }

  if (isError || !ticket) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-center text-xs text-rose-600">
        Unable to load this ticket.
      </div>
    );
  }

  const ownerName = formatAuthorName(ticketOwner, ticketOwner?.email || `User #${ticket.userId}`);
  const statusLabel = statusLabels[ticket.status] || ticket.status;

  return (
    <div className="mx-auto max-w-3xl">
      <button
        type="button"
        onClick={() => navigate("/tickets")}
        className="mb-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700"
      >
        <FiArrowLeft aria-hidden="true" />
        Back to tickets
      </button>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-medium text-slate-400">AZ-{String(ticket.id).padStart(4, "0")}</p>
            <h1 className="mt-1 text-lg font-medium text-slate-700">{ticket.title}</h1>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${statusStyles[ticket.status] || statusStyles.closed}`}>
            {statusLabel}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[10px]">
          <span className="rounded-full bg-indigo-50 px-2 py-1 text-indigo-500">
            {ticket.type === "aizoadviser" ? "AizoAdviser" : "AizoPlan"}
          </span>
          {ticket.category && (
            <span className={`rounded-full px-2 py-1 capitalize ${categoryStyles[ticket.category] || "bg-slate-100 text-slate-500"}`}>
              {ticket.category}
            </span>
          )}
          {ticket.issue && <span className="text-slate-400">{ticket.issue}</span>}
        </div>

        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="mt-3 flex flex-wrap gap-4 text-[10px] text-slate-400">
            <span>Client: {ownerName}{ticketOwner?.email ? ` (${ticketOwner.email})` : ""}</span>
            {ticketOwner?.organization?.name && <span>Organization: {ticketOwner.organization.name}</span>}
            <span>Created: {formatDate(ticket.createdAt)}</span>
            {ticket.resolvedAt && <span className="text-emerald-600">Resolved: {formatDate(ticket.resolvedAt)}</span>}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
          <span className="mr-1 text-[10px] text-slate-400">Move to</span>
          {statusOptions.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => handleStatusChange(status)}
              disabled={statusMutation.isPending}
              className={`rounded-full border px-2.5 py-1 text-[10px] transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                ticket.status === status
                  ? statusStyles[status]
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-400"
              }`}
            >
              {statusLabels[status]}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-3 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-xs font-medium text-slate-500">Conversation</h2>
        <div className="space-y-4">
          <ActivityMessage
            author={ticketOwner}
            authorType="client"
            message={ticket.description}
            date={ticket.createdAt}
            attachments={ticket.attachments || []}
          />
          {replies.map((reply) => (
            <ActivityMessage
              key={reply.id}
              author={reply.author || usersById.get(String(reply.userId))}
              authorType={reply.authorType}
              message={reply.message}
              date={reply.createdAt}
              attachments={reply.attachments}
            />
          ))}
        </div>

        {canReply ? (
          <form onSubmit={handleSubmit} className="mt-5 border-t border-slate-100 pt-4">
            <label htmlFor="ticket-reply" className="sr-only">Reply to client</label>
            <textarea
              id="ticket-reply"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={3}
              placeholder="Reply to the client..."
              className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-600 outline-none placeholder:text-slate-400 focus:border-slate-400"
            />
            <div className="mt-2">
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-slate-500">
                <FiPaperclip aria-hidden="true" />
                Attach files
                <input type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={handleAttachmentChange} className="sr-only" />
              </label>
              <p className="mt-1 text-[10px] text-slate-400">Up to 5 files, 10 MB each.</p>
              {attachments.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {attachments.map((file) => (
                    <li key={`${file.name}-${file.lastModified}`} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600">
                      <span className="truncate">{file.name}</span>
                      <button type="button" onClick={() => removeAttachment(file)} aria-label={`Remove ${file.name}`} className="text-slate-400 hover:text-rose-500"><FiX aria-hidden="true" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={replyMutation.isPending || (!message.trim() && !attachments.length)}
                className="inline-flex items-center gap-1.5 rounded-md bg-cyan-700 px-3 py-2 text-[10px] font-medium text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiSend aria-hidden="true" />
                {replyMutation.isPending ? "Sending..." : "Reply"}
              </button>
            </div>
          </form>
        ) : (
          <p className="mt-5 border-t border-slate-100 pt-4 text-[11px] text-slate-400">
            This ticket is {statusLabel.toLowerCase()} and cannot receive new replies.
          </p>
        )}
      </section>
    </div>
  );
}

export default TicketDetails;
